import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Swords, Coins, Clock, Trophy, X, Check, Sparkles } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import BottomNav from "../components/BottomNav.jsx";
import api from "../lib/api.js";
import { toast } from "sonner";
import { goalConfetti, chime } from "../lib/celebrate.js";

const STATUS = {
  active: { label: "Live", color: "#4ade80" },
  pending: { label: "Awaiting accept", color: "#FBBF24" },
  completed: { label: "Completed", color: "#a78bfa" },
  rejected: { label: "Declined", color: "#64748b" },
  cancelled: { label: "Cancelled", color: "#64748b" },
};

export default function DuelDetail() {
  const { duelId } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/duels/${duelId}`).then((r) => setD(r.data)).catch(() => nav("/duels"));

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [duelId]); // eslint-disable-line

  if (!d) {
    return <PhoneFrame><div className="p-6 text-slate-400 ps-serif ps-italic">Loading…</div></PhoneFrame>;
  }

  const me = d.challenger.is_me ? d.challenger : d.challenged;
  const opp = d.challenger.is_me ? d.challenged : d.challenger;
  const total = (me.steps || 0) + (opp.steps || 0);
  const myPct = total > 0 ? (me.steps / total) * 100 : 50;
  const oppPct = 100 - myPct;
  const tie = me.steps === opp.steps;
  const myLead = me.steps > opp.steps;
  const stat = STATUS[d.status];

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/duels/${d.id}/accept`);
      goalConfetti(); chime();
      toast.success("Duel accepted — game on!");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept");
    } finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try { await api.post(`/duels/${d.id}/reject`); toast("Duel declined"); nav("/duels"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true);
    try { await api.post(`/duels/${d.id}/cancel`); toast("Duel cancelled"); nav("/duels"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <PhoneFrame>
      <div className="ps-aurora min-h-screen">
        <div className="px-5 pt-6 pb-28">
          <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="duel-back">
            <ChevronLeft size={16} /> Back
          </button>

          <div className="mt-5 ps-fade-up">
            <div className="ps-chip mb-3" style={{ background: `${stat.color}22`, color: stat.color, borderColor: `${stat.color}55` }}>
              <Sparkles size={11} /> {stat.label}
            </div>
            <h1 className="ps-heading text-3xl font-extrabold leading-tight">
              {me.name.split(" ")[0]} <span className="ps-serif ps-italic font-normal text-slate-400">vs</span> {opp.name.split(" ")[0]}
            </h1>
            <div className="text-xs text-slate-400 mt-1 inline-flex items-center gap-3 ps-tabular">
              <span className="inline-flex items-center gap-1"><Coins size={12} color="#FBBF24" />{d.stake} pts each</span>
              <span className="inline-flex items-center gap-1"><Clock size={12} color="#a78bfa" />{d.duration_hours}h</span>
            </div>
            {d.message && (
              <p className="mt-3 text-sm text-slate-300 ps-serif ps-italic bg-[#0E1F2A]/40 border border-[#1B364A] rounded-2xl p-3" data-testid="duel-message">
                "{d.message}"
              </p>
            )}
          </div>

          {/* Head to head */}
          <div className="mt-6 ps-card p-5 relative overflow-hidden">
            <div className="absolute -top-12 -left-12 w-40 h-40 rounded-full blur-3xl bg-[#4ade80]/15" />
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl bg-[#ef4444]/15" />
            <div className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <Player u={me} accent="#4ade80" lead={d.status === "active" && myLead} bigLeft />
              <div className="text-center">
                <Swords size={22} className="mx-auto text-slate-400" />
                <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-1 font-bold">VS</div>
              </div>
              <Player u={opp} accent="#ef4444" lead={d.status === "active" && !myLead && !tie} />
            </div>
            {/* progress bar split */}
            <div className="relative mt-5 h-3 rounded-full bg-[#0E1F2A] border border-[#1B364A] overflow-hidden flex">
              <div className="h-full bg-gradient-to-r from-[#4ade80] to-[#22d3ee]" style={{ width: `${myPct}%`, transition: "width 0.7s ease" }} />
              <div className="h-full bg-gradient-to-r from-[#a78bfa] to-[#ef4444]" style={{ width: `${oppPct}%`, transition: "width 0.7s ease" }} />
            </div>
            <div className="flex justify-between text-[10px] text-slate-500 ps-tabular mt-1">
              <span>{Math.round(myPct)}%</span>
              <span>{Math.round(oppPct)}%</span>
            </div>
            {d.status === "active" && d.ends_at && (
              <div className="mt-3 text-center text-xs text-slate-400 ps-serif ps-italic" data-testid="duel-countdown">
                <Countdown ends={d.ends_at} />
              </div>
            )}
            {d.status === "completed" && (
              <div className="mt-4 text-center" data-testid="duel-outcome">
                {d.winner_id === me.id ? (
                  <div className="inline-flex items-center gap-2 text-[#4ade80] font-extrabold ps-heading text-lg">
                    <Trophy size={20} /> You won +{d.stake * 2 + 50}
                  </div>
                ) : d.winner_id === opp.id ? (
                  <div className="inline-flex items-center gap-2 text-[#ef4444] font-extrabold ps-heading text-lg">
                    <X size={20} /> {opp.name} took it
                  </div>
                ) : (
                  <div className="text-slate-300 ps-serif ps-italic">Tied — stakes refunded.</div>
                )}
              </div>
            )}
          </div>

          {/* Action zone */}
          {d.status === "pending" && d.challenged.is_me && (
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button onClick={accept} disabled={busy} data-testid="duel-accept" className="ps-button-primary inline-flex items-center justify-center gap-1.5">
                <Check size={16} /> Accept
              </button>
              <button onClick={reject} disabled={busy} data-testid="duel-reject" className="ps-button-secondary inline-flex items-center justify-center gap-1.5">
                <X size={16} /> Decline
              </button>
            </div>
          )}
          {d.status === "pending" && d.challenger.is_me && (
            <button onClick={cancel} disabled={busy} data-testid="duel-cancel" className="mt-5 w-full ps-button-secondary inline-flex items-center justify-center gap-1.5">
              <X size={16} /> Cancel challenge
            </button>
          )}
        </div>
      </div>
      <BottomNav />
    </PhoneFrame>
  );
}

function Player({ u, accent, lead, bigLeft }) {
  return (
    <div className={`flex flex-col items-center text-center ${bigLeft ? "" : ""}`}>
      <div
        className="w-16 h-16 rounded-full bg-gradient-to-br from-[#15303E] to-[#0E1F2A] border-2 flex items-center justify-center font-bold text-2xl ps-heading"
        style={{ borderColor: lead ? accent : "#1B364A", boxShadow: lead ? `0 0 24px ${accent}55` : "none" }}
      >
        {u.name?.[0]?.toUpperCase()}
      </div>
      <div className="mt-2 text-xs font-bold truncate max-w-[100px]">{u.is_me ? "You" : u.name.split(" ")[0]}</div>
      <div className="ps-heading text-2xl font-extrabold ps-tabular mt-0.5" style={{ color: lead ? accent : "#cbd5e1" }} data-testid={u.is_me ? "duel-my-steps" : "duel-opp-steps"}>
        {(u.steps || 0).toLocaleString()}
      </div>
      <div className="text-[9px] text-slate-500 uppercase tracking-widest">steps</div>
    </div>
  );
}

function Countdown({ ends }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = new Date(ends).getTime() - now;
  if (ms <= 0) return <>Finishing up…</>;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return <>Time remaining — <span className="text-white ps-tabular font-bold">{h}h {String(m).padStart(2, "0")}m {String(s).padStart(2, "0")}s</span></>;
}
